
import React, { useState, useEffect, useRef } from 'react';
import { TextField, Paper, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Typography, Tooltip } from '@mui/material';
import { createPortal } from 'react-dom';
import axios from 'axios';

const TagModal = ({ open, position, onClose, subfolderId, cardId, cardQuestionType, onTagAdded, onTagUpdated, onTagDeleted, onShowSnackbar }) => {
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState([]);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagValue, setEditingTagValue] = useState('');
  const modalRef = useRef();
  const apiUrl = process.env.REACT_APP_BASE_URL ;

  useEffect(() => {
    if (open) {
      setTag('');
      fetchTags();
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const fetchTags = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found in sessionStorage');
        onShowSnackbar('Authentication token missing. Please log in again.', 'error');
        return;
      }
      const response = await axios.get(`${apiUrl}/subfolder/${subfolderId}/tags/${cardQuestionType}/${cardId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags from server:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
        } : null,
      });
      onShowSnackbar('Failed to fetch tags. Please try again.', 'error');
    }
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

const handleAddTag = async (event) => {
  if (event.key !== 'Enter' || !tag.trim()) return;

  const sanitizedTag = tag.trim();
  if (!sanitizedTag) {
    onShowSnackbar('Tag name cannot be empty.', 'error');
    return;
  }

  const token = sessionStorage.getItem('authToken');
  if (!token) {
    onShowSnackbar('Please log in again.', 'error');
    return;
  }

const payload = { tags: [{ name: sanitizedTag }] };

  try {
    const response = await axios.post(
      `${apiUrl}/subfolder/${subfolderId}/tags/${cardQuestionType}/${cardId}/`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    const newTag = response.data.tags?.[0] || { name: sanitizedTag, id: null };
    setTags((prevTags) => [...prevTags, newTag]);
    onTagAdded(cardId, newTag);
    setTag('');
    onShowSnackbar('Tag added successfully!', 'success');
    onClose(); // Consistent modal closing
  } catch (error) {
    const status = error.response?.status;
    let message = 'Failed to add tag. Please try again.';
    if (status === 401) {
      message = 'Session expired. Please log in again.';
    } else if (status === 400) {
      message = error.response?.data?.message || 'Invalid tag name.';
    }
    onShowSnackbar(message, 'error');
    console.error('Tag creation failed:', {
      message: error.message,
      status,
      data: error.response?.data,
    });
  }
};

  const handleEditTagStart = (tagId, currentTagName) => {
    setEditingTagId(tagId);
    setEditingTagValue(currentTagName);
  };

  const handleEditTagChange = (event) => {
    setEditingTagValue(event.target.value);
  };

  const handleEditTagSave = async () => {
    if (!editingTagValue.trim()) {
      console.error('Tag name cannot be empty');
      onShowSnackbar('Tag name cannot be empty.', 'error');
      return;
    }

    try {
      const token = sessionStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found in sessionStorage');
        onShowSnackbar('Authentication token missing. Please log in again.', 'error');
        return;
      }
      const payload = {
        tags: [{ name: editingTagValue.trim() }],
      };
      const response = await axios.put(
        `${apiUrl}/subfolder/${subfolderId}/tags/${cardQuestionType}/${cardId}/${editingTagId}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );
      console.log('Tag update response:', response.data);

      // Notify parent with updated tag data
      const updatedTag = response.data.tags?.[0] || { id: editingTagId, name: editingTagValue.trim() };
      onTagUpdated(cardId, updatedTag);
      setEditingTagId(null);
      setTags((prevTags) =>
        prevTags.map((tag) =>
          tag.id === editingTagId ? { ...tag, name: editingTagValue.trim() } : tag
        )
      );
      onShowSnackbar('Tag updated successfully!', 'success');
    } catch (error) {
      console.error('Failed to update tag on server:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
        } : null,
      });
      onShowSnackbar('Failed to update tag. Please try again.', 'error');
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      const token = sessionStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found in sessionStorage');
        onShowSnackbar('Authentication token missing. Please log in again.', 'error');
        return;
      }
      const response = await axios.delete(
        `${apiUrl}/subfolder/${subfolderId}/tags/${cardQuestionType}/${cardId}/${tagId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );
      console.log('Tag deletion response:', response.data);

      // Notify parent of tag deletion
      onTagDeleted(cardId, tagId);
      setTags((prevTags) => prevTags.filter((tag) => tag.id !== tagId));
      onShowSnackbar('Tag deleted successfully!', 'success');
    } catch (error) {
      console.error('Failed to delete tag from server:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
        } : null,
      });
      onShowSnackbar('Failed to delete tag. Please try again.', 'error');
    }
  };

  if (!open) return null;

  return createPortal(
    <Paper
      ref={modalRef}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        zIndex: 1000,
        padding: '10px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <TextField
        autoFocus
        margin="dense"
        variant="standard"
        label="Tag"
        type="text"
        fullWidth
        value={tag}
        onChange={(e) => setTag(e.target.value)}
        onKeyPress={handleAddTag}
      />
      <List>
        {tags.map((tagItem) => (
          <ListItem key={tagItem.id}>
            {editingTagId === tagItem.id ? (
              <>
                <TextField
                  value={editingTagValue}
                  onChange={handleEditTagChange}
                  onBlur={handleEditTagSave}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleEditTagSave();
                    }
                  }}
                  autoFocus
                  variant="standard"
                  fullWidth
                />
              </>
            ) : (
              <>
                <ListItemText primary={`🏷️${tagItem.name}`} sx={{ margin: 0 }} />
                <ListItemSecondaryAction>
                  <Tooltip title="Edit Tag" placement="top">
                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditTagStart(tagItem.id, tagItem.name)}>
                      <Typography>✏️</Typography>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Tag" placement="top">
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteTag(tagItem.id)}>
                      <Typography>🗑️</Typography>
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </>
            )}
          </ListItem>
        ))}
      </List>
    </Paper>,
    document.body
  );
};

export default TagModal;
